import { useCallback } from "react"

import {
  useMutation,
  useQueryClient,
  type MutateOptions,
} from "@tanstack/react-query"
import { useEventCallback } from "usehooks-ts"

import { logger } from "@/lib/logger"
import {
  getTransportOperationName,
  getTransportOperationVersion,
  type TransportMutationVariables,
  type TransportOperationInput,
  type TransportOperationName,
  type TransportOperationResponse,
  type TransportOperationVersionNumber,
} from "@/types_api/backend/transport/operations"

import {
  TransportContextNotReadyError,
  type TransportRequestError,
} from "../utils/transportClient"
import { reportTransportError } from "../utils/transportErrorReporting"
import {
  applyTransportMutationCacheEffects,
  getTransportMutationCacheEffects,
  type TransportMutationCacheEffects,
  type TransportMutationCacheScope,
} from "./transportMutationCacheEffects"
import {
  executeTransportOperation,
  isTransportOperationReady,
  transportOperations,
} from "./transportOperationRuntime"
import type {
  TransportMutationOptions,
  TransportMutationOptionsWithOptionalVersion,
  TransportMutationResult,
  TransportVersionedMutationOptions,
  TransportVersionedOperationGroup,
  TransportVersionedOperationName,
} from "./transportQueryTypes"
import { useTransportClient } from "./useTransportClient"

const log = logger("TransportMutation")

interface MutationInvocation<
  Name extends TransportOperationName,
  TContext = unknown,
> {
  operation: Name
  request: TransportMutationVariables<Name>
  getClient: ReturnType<typeof useTransportClient>["getClient"]
  isReady: boolean
  scope: TransportMutationCacheScope
  effects: TransportMutationCacheEffects | false | undefined
  executed: boolean
  effectsApplied: boolean
  onMutate: TransportMutationOptions<Name, TContext>["onMutate"]
}

function applyCacheEffects<Name extends TransportOperationName, TContext>(
  invocation: MutationInvocation<Name, TContext>,
  outcome: "success" | "error" = "success",
): Promise<void> | undefined {
  if (!invocation.effects || invocation.effectsApplied) {
    return
  }
  invocation.effectsApplied = true
  const { effects } = invocation
  const completion = Promise.resolve()
    .then(() =>
      applyTransportMutationCacheEffects(invocation.scope, effects, outcome),
    )
    .catch((error: unknown) => {
      log.error("Transport mutation cache effects failed", error, {
        transportOperation: invocation.operation,
      })
    })
  return effects.waitForRefetch && outcome === "success"
    ? completion
    : undefined
}

function invocationCallbacks<Name extends TransportOperationName, TContext>(
  options?: MutateOptions<
    TransportOperationResponse<Name>,
    TransportRequestError,
    TransportMutationVariables<Name>,
    TContext
  >,
): MutateOptions<
  TransportOperationResponse<Name>,
  TransportRequestError,
  MutationInvocation<Name, TContext>,
  TContext | undefined
> {
  return {
    onSuccess: (data, invocation, result, context) =>
      options?.onSuccess?.(data, invocation.request, result, context),
    onError: (error, invocation, result, context) =>
      options?.onError?.(error, invocation.request, result, context),
    onSettled: (data, error, invocation, result, context) =>
      options?.onSettled?.(data, error, invocation.request, result, context),
  }
}

export function useTransportMutation<
  Name extends TransportOperationName,
  TContext = unknown,
>(
  operation: TransportOperationInput<Name>,
  options?: TransportMutationOptions<Name, TContext>,
): TransportMutationResult<Name, TContext>

export function useTransportMutation<
  Group extends TransportVersionedOperationGroup,
  Version extends TransportOperationVersionNumber<Group>,
  Name extends TransportVersionedOperationName<Group, Version>,
  TContext = unknown,
>(
  operation: TransportOperationInput<Group>,
  options: TransportVersionedMutationOptions<Group, Version, TContext>,
): TransportMutationResult<Name, TContext>

export function useTransportMutation<
  Name extends TransportOperationName,
  TContext = unknown,
>(
  operation: TransportOperationInput<Name>,
  options?: TransportMutationOptionsWithOptionalVersion<Name, TContext>,
): TransportMutationResult<Name, TContext> {
  const baseOperationName = getTransportOperationName(operation)
  const operationName =
    options && "version" in options && options.version != null
      ? (getTransportOperationVersion(
          baseOperationName as TransportVersionedOperationGroup,
          options.version as TransportOperationVersionNumber<TransportVersionedOperationGroup>,
        ).name as Name)
      : baseOperationName
  const {
    cacheEffects,
    onMutate,
    onSuccess,
    onError,
    onSettled,
    reportErrors = true,
    version: _version,
    ...mutationOptions
  } = options ?? {}
  const queryClient = useQueryClient()
  const {
    getClient,
    isAuthLoaded,
    isOrganizationSynced,
    organizationSlug,
    workspaceId,
  } = useTransportClient()
  const authRequired = transportOperations[operationName].auth === "session"
  const organizationRequired =
    transportOperations[operationName].organizationRequired
  const isReady = isTransportOperationReady({
    authRequired,
    isAuthLoaded,
    isOrganizationSynced,
    organizationRequired,
    organizationSlug,
  })

  const mutation = useMutation<
    TransportOperationResponse<Name>,
    TransportRequestError,
    MutationInvocation<Name, TContext>,
    TContext | undefined
  >({
    ...mutationOptions,
    mutationFn: (invocation) => {
      if (!invocation.isReady) {
        throw new TransportContextNotReadyError(invocation.operation)
      }
      invocation.executed = true

      return executeTransportOperation(
        invocation.getClient(),
        invocation.operation,
        invocation.request,
      )
    },
    onMutate: (invocation, context) =>
      invocation.onMutate?.(invocation.request, context),
    onSuccess: async (data, invocation, result, context) => {
      try {
        await onSuccess?.(data, invocation.request, result as TContext, context)
      } finally {
        await applyCacheEffects(invocation)
      }
    },
    onError: async (error, invocation, result, context) => {
      try {
        if (reportErrors) {
          reportTransportError({
            error,
            kind: "mutation",
            operation: invocation.operation,
            organizationSlug: invocation.scope.organizationSlug,
          })
        }
        await onError?.(error, invocation.request, result, context)
      } finally {
        if (
          invocation.executed &&
          invocation.effects &&
          invocation.effects.invalidateOnError !== false
        ) {
          await applyCacheEffects(invocation, "error")
        }
      }
    },
    onSettled: (data, error, invocation, result, context) =>
      onSettled?.(data, error, invocation.request, result, context),
  })

  const createInvocation = useCallback(
    (
      request: TransportMutationVariables<Name>,
    ): MutationInvocation<Name, TContext> => ({
      operation: operationName,
      request,
      getClient,
      isReady,
      scope: { queryClient, organizationSlug, workspaceId },
      effects: cacheEffects ?? getTransportMutationCacheEffects(operationName),
      executed: false,
      effectsApplied: false,
      onMutate,
    }),
    [
      operationName,
      getClient,
      isReady,
      queryClient,
      organizationSlug,
      workspaceId,
      cacheEffects,
      onMutate,
    ],
  )
  const { mutate: mutateInvocation, mutateAsync: mutateInvocationAsync } =
    mutation
  const mutate = useEventCallback<
    Parameters<TransportMutationResult<Name, TContext>["mutate"]>,
    ReturnType<TransportMutationResult<Name, TContext>["mutate"]>
  >((request, callOptions) =>
    mutateInvocation(
      createInvocation(request),
      invocationCallbacks(callOptions),
    ),
  )
  const mutateAsync = useEventCallback<
    Parameters<TransportMutationResult<Name, TContext>["mutateAsync"]>,
    ReturnType<TransportMutationResult<Name, TContext>["mutateAsync"]>
  >((request, callOptions) =>
    mutateInvocationAsync(
      createInvocation(request),
      invocationCallbacks(callOptions),
    ),
  )

  return {
    ...mutation,
    variables: mutation.variables?.request,
    mutate,
    mutateAsync,
    isReady,
  } as TransportMutationResult<Name, TContext>
}
